import { sumBy } from 'lodash';
import CouponHandler from './CouponHandler';

class CartHandler {
  constructor(cart = [], customer = null) {
    this.cart = cart; // Array of cart items
    this.customer = customer; // Logged-in customer data (null for guests)
    this.coupons = []; // Applied coupons
    this.shipping = null; // Selected shipping method
    this.taxRate = 0; // Tax Rate
    this.grossTotal = 0
    this.netTotal = 0
    this.totalDiscounts = 0
    this.couponHandler = this.getCouponHandler();
  }

  getCouponHandler(){
    this.couponHandler = new CouponHandler(this.cart, this.coupons);
    return this.couponHandler
  }

  recalculateAll() {
    // Recalculate all coupons
    this.coupons = this.coupons.map(coupon => {
      try {
        const discount = this.couponHandler.calculateDiscount();
        return { ...coupon, discount };
      } catch (error) {
        // If coupon becomes invalid, remove it
        console.warn(`Coupon ${coupon.code} removed: ${error.message}`);
        return null;
      }
    }).filter(Boolean); // Remove invalid coupons

    // Update totals
    this.grossTotal = this.getGrossTotal();
    this.netTotal = this.getNetTotal();
    this.totalDiscounts = this.getTotalDiscount();
  }

  /** Adds an item to the cart */
  addItem(product) {
    if (!product?.id || typeof product.price === 'undefined') {
      console.error('Invalid product data:', product);
      return;
    }

    const existingItemIndex = this.cart.findIndex(item => item.variation_id === product.variation_id);
    
    if (existingItemIndex != -1) {
      const updatedCart = [...this.cart];
      updatedCart[existingItemIndex] = {
        ...updatedCart[existingItemIndex],
        quantity: updatedCart[existingItemIndex].quantity + product.quantity
      };
      this.cart = updatedCart;
    } else {
      this.cart = [...this.cart, { ...product }];
    }

    this.recalculateAll();
  }

  /** Removes an item from the cart */
  removeItem(productId) {
    if (!productId) {
      console.error('Invalid productId:', productId);
      return;
    }
    this.cart = this.cart.filter(item => item.variation_id !== productId);

    this.recalculateAll();
  }

  /** Updates quantity of an item */
  updateItemQuantity(productId, quantity) {
    if (!productId || quantity < 0) {
      console.error('Invalid update parameters:', { productId, quantity });
      return;
    }

    if (quantity === 0) {
      this.removeItem(productId);
      return;
    }

    this.cart = this.cart.map(item =>
      item.variation_id === productId ? { ...item, quantity } : item
    );

    this.recalculateAll();
  }

  /** Clears the cart */
  clearCart() {
    this.cart = [];
    this.coupons = [];

    this.recalculateAll();
  }

  isInCart(product) {
    return this.cart.some(item => item.variation_id === product.variation_id);
  }

  /** Calculates subtotal (before tax, shipping, and discounts) */
  getSubtotal() {
    return this.cart.reduce((total, item) => {
      const itemPrice = parseFloat(item.price);
      const itemQuantity = parseInt(item.quantity, 10);
      
      if (isNaN(itemPrice) || isNaN(itemQuantity)) {
        console.error('Invalid price or quantity:', item);
        return total;
      }
      
      return total + itemPrice * itemQuantity;
    }, 0);
  }

  getTotalItems() {
    return this.cart.reduce((total, item) => {
      const itemQuantity = parseInt(item.quantity, 10);
      return isNaN(itemQuantity) ? total : total + itemQuantity;
    }, 0);
  }

  getItemQuantity(productId) {
    const item = this.cart.find(item => item.id === productId);
    return item ? item.quantity : 0;
  }

  /** Applies a WooCommerce coupon */
  applyCoupon(couponData) {
    try {

      const hasAppliedCouponCode = this.coupons.some(coupon => coupon.code === couponData.code);
      
      if (hasAppliedCouponCode) {
        throw new Error('This coupon has already been applied to your cart');
      }

      const couponHandler = new CouponHandler(this.cart, couponData);
      const discount = couponHandler.calculateDiscount();
      this.coupons.push({ ...couponData, discount });
      console.log(this)
    } catch (error) {
      throw new Error(error.message);
    }
  }

  /** Removes a coupon */
  removeCoupon(code) {
    this.coupons = this.coupons.filter(coupon => coupon.code !== code);
    this.recalculateAll();
  }

  /** Calculates total discount from all applied coupons */
  getTotalDiscount() {
    return sumBy(this.coupons, 'discount');
  }

  /** Calculates tax amount based on subtotal */
  getTaxAmount() {
    return (this.getSubtotal() - this.getTotalDiscount()) * this.taxRate;
  }

  /** Calculates shipping cost (adjust based on WooCommerce shipping API) */
  getShippingCost() {
    return this.shipping ? this.shipping.cost : 0;
  }

  /** Calculates final total (subtotal - discounts + tax + shipping) */
  getGrossTotal() {
    return this.getSubtotal() + this.getTaxAmount() + this.getShippingCost();
  }
  
  /** Calculates final total (subtotal - discounts + tax + shipping) */
  getNetTotal() {
    let total = this.getSubtotal() - this.getTotalDiscount() + this.getTaxAmount() + this.getShippingCost();
    return total < 0 ? 0 : total;
  }

}

export default CartHandler;
